import type { FC, ReactNode } from 'react'
import type { NavCollection } from '@/types'

import Link from 'next/link'
import { cms } from '@/lib/cms'
import { telHref } from '@/lib/format'
import { NewsletterForm } from '@/components/generals/newsletter-form'
import { clientServices } from '@/constants/site'

interface FooterProps {
    navigation: Array<NavCollection>
}

const MAISON_LINKS = [
    { label: 'Our story', href: '/maison' },
    { label: 'Savoir-faire', href: '/maison#savoir-faire' },
    { label: 'History', href: '/maison#history' },
    { label: 'Commitments', href: '/maison#commitments' }
]

const SERVICE_LINKS = [
    { label: 'Book an appointment', href: '/appointment' },
    { label: 'Find a boutique', href: '/boutiques' },
    { label: 'Your bag', href: '/bag' },
    { label: 'Privacy policy', href: '/privacy' }
]

const linkClasses = 'text-sm text-background/70 transition-colors hover:text-background'

export const Footer: FC<FooterProps> = ({ navigation }): ReactNode => {
    return (
        <footer id='footer' className='bg-foreground text-background'>
            <div className='mx-auto max-w-[1600px] px-6 pt-20 sm:px-10 lg:px-16 lg:pt-28'>
                <div className='grid gap-16 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <p className='eyebrow text-background/60'>
                            Newsletter
                        </p>

                        <h2 className='mt-5 font-heading text-3xl font-light leading-tight sm:text-4xl'>
                            Receive news from the Maison
                        </h2>

                        <p className='mt-4 max-w-md text-[15px] leading-relaxed text-background/70'>
                            New collections, private previews and stories from our ateliers, a few times a season.
                        </p>

                        <NewsletterForm />
                    </div>

                    <div className='grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-4 lg:col-span-7'>
                        <nav aria-label='Collections'>
                            <p className='eyebrow text-background/60'>
                                Collections
                            </p>

                            <ul className='mt-6 flex flex-col gap-3'>
                                {navigation.map(collection => (
                                    <li key={collection.id}>
                                        <Link href={`/collections/${collection.slug}`} className={linkClasses}>
                                            <span data-mdlfy-cms={cms('collections', collection.id, 'name')}>
                                                {collection.name}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <nav aria-label='The Maison'>
                            <p className='eyebrow text-background/60'>
                                The Maison
                            </p>

                            <ul className='mt-6 flex flex-col gap-3'>
                                {MAISON_LINKS.map(link => (
                                    <li key={link.href}>
                                        <Link href={link.href} className={linkClasses}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <nav aria-label='Client services'>
                            <p className='eyebrow text-background/60'>
                                Client services
                            </p>

                            <ul className='mt-6 flex flex-col gap-3'>
                                {SERVICE_LINKS.map(link => (
                                    <li key={link.href}>
                                        <Link href={link.href} className={linkClasses}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div>
                            <p className='eyebrow text-background/60'>
                                Contact
                            </p>

                            <div className='mt-6 flex flex-col gap-3 text-sm leading-relaxed text-background/70'>
                                <a href={telHref(clientServices.phone)} className='transition-colors hover:text-background'>
                                    {clientServices.phone}
                                </a>

                                <p>
                                    {clientServices.hours}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='mt-24 border-t border-background/15 pt-12 lg:mt-32'>
                    <p aria-hidden className='select-none whitespace-nowrap text-center font-heading text-[min(19vw,18rem)] font-light uppercase leading-[0.8] tracking-[0.12em] text-background/90'>
                        Vaurel
                    </p>
                </div>

                <div className='flex flex-col gap-3 py-8 text-xs text-background/60 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> Maison Vaurel, Paris. All rights reserved.
                    </p>

                    <p>
                        International, English. Prices in euros.
                    </p>
                </div>
            </div>
        </footer>
    )
}
